import { CheckCircle } from 'lucide-react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Card as CardType } from '../../service/api';
import useTasksStore from '../../store/tasks';
import CardActionsList from '../CardActionsList';
import ProgressBar from '../ProgressBar';

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  icon: IconName;
}

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  isOpen,
  onClose,
  taskId,
  icon,
}) => {
  const { getTaskById, completeTask } = useTasksStore();
  const { t } = useTranslation();

  const task = getTaskById(taskId);
  if (!task || !isOpen) return null;

  const {
    title,
    description,
    progress,
    isActive,
    recurrenceId,
    start,
    rewardCard,
  } = task;

  const hasActions =
    typeof rewardCard !== 'string' &&
    (rewardCard as CardType).actions?.length > 0;

  const isCompleted = progress >= 100;
  const normalizedProgress = Math.min(Math.max(progress, 0), 100);

  const handleComplete = async () => {
    try {
      await completeTask(taskId, recurrenceId);
      onClose();
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  const progressBar = (
    <div className="mb-4">
      <h6 className="text-white-50 mb-2">{t('tasks.timeElapsed')}</h6>
      <ProgressBar now={normalizedProgress}></ProgressBar>
      <div className="d-flex justify-content-between mt-1">
        <small className="text-white-50">{normalizedProgress}%</small>
        <small className="text-white-50">
          {isCompleted
            ? t('tasks.completed')
            : isActive
              ? t('tasks.inProgress')
              : t('tasks.scheduled')}
        </small>
      </div>
    </div>
  );

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      size="lg"
      className="fs-3"
      contentClassName="position-relative"
      backdrop="static"
    >
      <Modal.Dialog
        className="m-0"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div
          className="position-absolute top-0 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-primary"
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(6, 56, 96, 0.95)',
            zIndex: 1,
          }}
        >
          {isCompleted ? (
            <CheckCircle size={32} strokeWidth={2} className="text-success" />
          ) : (
            <DynamicIcon
              name={icon}
              size={32}
              strokeWidth={1.5}
              className="text-white"
            />
          )}
        </div>
        <Modal.Header
          closeButton
          closeVariant="white"
          className="border-0 pt-5 pb-3"
          style={{ backgroundColor: 'transparent' }}
        >
          <Modal.Title className="fs-1 text-white fw-bold w-100 text-center">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="text-white px-4 pb-4"
          style={{ backgroundColor: 'transparent' }}
        >
          {description && (
            <div className="mb-4">
              <h6 className="text-white-50 mb-2">{t('common.description')}</h6>
              <p className="mb-0">{description}</p>
            </div>
          )}

          {hasActions && (
            <div className="mb-4">
              <h6 className="text-white-50 mb-3 fs-4">{t('common.reward')}</h6>
              <div
                className="d-flex align-items-center justify-content-center gap-3 p-4 rounded-3"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <CardActionsList
                  actions={(rewardCard as CardType).actions}
                  display="full"
                  size="lg"
                />
              </div>
            </div>
          )}

          {isActive || (
            <div className="mb-4">
              <h6 className="text-white-50 mb-2">{t('tasks.start')}</h6>
              <p className="mb-0">
                {start.toLocal().toLocaleString({
                  dateStyle: 'long',
                  timeStyle: 'medium',
                })}
              </p>
            </div>
          )}

          {isActive && progressBar}

          {!isCompleted && isActive && (
            <div className="text-center">
              <Button
                variant="success"
                className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                style={{
                  width: '80px',
                  height: '80px',
                  fontSize: '1.5rem',
                }}
                onClick={handleComplete}
                aria-label={t('tasks.markAsCompleted')}
              >
                <CheckCircle size={40} strokeWidth={2} />
              </Button>
              <p className="mt-3 text-white-50 small">
                {t('tasks.markAsCompleted')}
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  );
};

export default TaskDetailsModal;
export type { TaskDetailsModalProps };
